import type { AxiosRequestConfig, AxiosResponse } from 'axios';

/**
 * Журнал действий с интеграциями (us/11024).
 *
 * Пользователь сообщает о проблеме одной фразой — «не подключается», «нет
 * QR», — и разбирать приходится задним числом по логам шлюзов. Журнал пишет
 * то, чего в переписке никогда не бывает: что именно нажали, что ушло на
 * сервер и что он ответил.
 *
 * Запись включается на странице реестра интеграций и выключается при уходе с
 * неё: журнал живёт только в памяти вкладки и в хранилища браузера не
 * попадает — в нём лежат ответы сервера.
 */

/** Действие пользователя, распознанное по адресу запроса. */
export type IntegrationActionKind =
  | 'restart'
  | 'request-qr'
  | 'get-qr'
  | 'pairing-code'
  | 'auth-state'
  | 'settings'
  | 'create'
  | 'delete'
  | 'branch-switch'
  | 'status-change'
  | 'other';

export interface IntegrationLogEntry {
  /** Время события в ISO, чтобы файл читался без пересчёта часовых поясов. */
  at: string;
  kind: IntegrationActionKind;
  method: string;
  url: string;
  /** Параметры строки запроса и тело — уже без секретов. */
  params?: unknown;
  body?: unknown;
  status?: number;
  /** Ответ сервера — тоже после маскирования. */
  response?: unknown;
  /** Сколько занял запрос, мс. */
  durationMs?: number;
  /** Сообщение об ошибке, если запрос не дошёл. */
  error?: string;
}

/**
 * Ключи, значения которых в журнал не попадают ни при каких условиях. Файл
 * пользователь отправляет в переписку, и он не должен становиться способом
 * передать доступы. Сравнение по вхождению и без регистра: у одного и того же
 * поля в разных ответах встречается и `Token`, и `accessToken`.
 */
const SECRET_KEYS = [
  'authorization',
  'token',
  'password',
  'secret',
  'cookie',
  'accesscode',
  'apikey',
  'session',
  'qrcode',
  'pairingcode',
  'logincode',
  'authcode',
];

/** Значение вместо секрета: видно, что поле было, но не видно, чему равно. */
const MASK = '***';

/** Больше этого журнал не растёт — вкладка живёт часами, память не резиновая. */
const MAX_ENTRIES = 500;

/** Длинные строки (например, QR-код в base64) в файле бесполезны и опасны. */
const MAX_STRING_LENGTH = 2_000;

interface MaskOptions {
  /** Ответы QR-эндпоинтов кладут чувствительное значение в общий JsonData. */
  maskJsonData?: boolean;
  /** IntegrationAuth принимает телефон, код, пароль и bot token в поле Value. */
  maskAuthValue?: boolean;
  /** RequestWhatsAppPairingCode возвращает одноразовый код в поле Code. */
  maskPairingCode?: boolean;
  /** Некоторые шлюзовые ответы возвращают QR/login URL строкой целиком. */
  maskRootString?: boolean;
}

/** Возвращает разобранный JSON или undefined, если строка им не является. */
const tryParseJson = (value: string): unknown => {
  const trimmed = value.trim();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return undefined;

  try {
    return JSON.parse(trimmed);
  } catch {
    return undefined;
  }
};

const normalizeKey = (key: string) =>
  key.toLowerCase().replace(/[^a-zа-я0-9]/g, '');

const isSecretKey = (key: string, options: MaskOptions) => {
  const lowered = normalizeKey(key);

  if (lowered === 'qr') return true;
  if (options.maskJsonData && lowered === 'jsondata') return true;
  if (options.maskAuthValue && lowered === 'value') return true;
  if (options.maskPairingCode && lowered === 'code') return true;

  return SECRET_KEYS.some((secret) => lowered.includes(secret));
};

/**
 * Копирует значение, вырезая секреты и обрезая длинные строки. Обходит только
 * вложенность разумной глубины: ответы CRM плоские, а зацикленные ссылки в
 * журнале не нужны.
 */
const maskValue = (
  value: unknown,
  depth = 0,
  options: MaskOptions = {}
): unknown => {
  if (depth > 6) return '[глубже не пишем]';

  if (typeof value === 'string') {
    if (depth === 0 && options.maskRootString) return MASK;

    // CRM кладёт полезную часть ответа строкой в поле JsonData, и секреты
    // прячутся именно там. Разбираем такую строку и маскируем внутри, иначе
    // токены уезжали бы в файл как есть.
    const parsed = tryParseJson(value);
    if (parsed !== undefined) return maskValue(parsed, depth + 1, options);

    return value.length > MAX_STRING_LENGTH
      ? `[длинная строка скрыта, длина ${value.length}]`
      : value;
  }

  if (Array.isArray(value)) {
    return value.map((item) => maskValue(item, depth + 1, options));
  }

  if (value && typeof value === 'object') {
    return Object.entries(value as Record<string, unknown>).reduce<
      Record<string, unknown>
    >((acc, [key, item]) => {
      acc[key] =
        item !== null && isSecretKey(key, options)
          ? MASK
          : maskValue(item, depth + 1, options);

      return acc;
    }, {});
  }

  return value;
};

const QR_RESPONSE_URL_PATTERN =
  /(?:GetQr|RefreshIntegrationAuthData|RequestWhatsAppPairingCode)/i;
const PAIRING_CODE_URL_PATTERN = /RequestWhatsAppPairingCode/i;
const AUTH_VALUE_URL_PATTERN =
  /\/IntegrationAuth\/(?:SendPhone|SendCode|SendPassword|SetBotToken)/i;

const maskRequestValue = (url: string, value: unknown) =>
  maskValue(value, 0, {
    maskAuthValue: AUTH_VALUE_URL_PATTERN.test(url),
  });

const maskResponseValue = (url: string, value: unknown) => {
  const hasSensitiveAuthPayload = QR_RESPONSE_URL_PATTERN.test(url);

  return maskValue(value, 0, {
    maskJsonData: hasSensitiveAuthPayload,
    maskPairingCode: PAIRING_CODE_URL_PATTERN.test(url),
    maskRootString: hasSensitiveAuthPayload,
  });
};

/** Не даёт секретам в query string и URL userinfo попасть в поле url/error. */
const maskUrlSecrets = (value: string) =>
  value
    .replace(/:\/\/[^/@\s:]+:[^/@\s]+@/g, `://${MASK}:${MASK}@`)
    .replace(
      /([?&](?:authorization|access_?token|refresh_?token|token|password|secret|api_?key|code)=)[^&#\s]*/gi,
      `$1${MASK}`
    )
    .replace(/(Bearer\s+)[^\s,;]+/gi, `$1${MASK}`);

const ERROR_SECRET_ASSIGNMENT_PATTERN = new RegExp(
  `((?:["']?(?:authorization|access[_-]?token|refresh[_-]?token|token|password|secret|cookie|api[_-]?key|session|qr(?:code)?|pairing[_-]?code|login[_-]?code|auth[_-]?code|access[_-]?code)["']?)\\s*[:=]\\s*)(["'][^"']*["']|[^\\s,;}\\]]+)`,
  'gi'
);

/**
 * Axios/шлюз иногда кладёт тело ответа прямо в Error.message. Это уже не URL,
 * поэтому query-only маска не видела `token=...`, JSON и `password: ...`.
 * Числовой `code 403` намеренно сохраняем: маскируются только явно
 * чувствительные разновидности кода (pairing/login/auth/access).
 */
const maskErrorSecrets = (value: string) => {
  const urlMasked = maskUrlSecrets(value);
  const parsed = tryParseJson(urlMasked);
  if (parsed !== undefined) {
    return JSON.stringify(
      maskValue(parsed, 0, {
        maskAuthValue: true,
        maskJsonData: true,
      })
    );
  }

  const masked = urlMasked.replace(
    ERROR_SECRET_ASSIGNMENT_PATTERN,
    (_match, prefix: string, secret: string) => {
      const quote = secret.startsWith('"')
        ? '"'
        : secret.startsWith("'")
        ? "'"
        : '';

      return `${prefix}${quote}${MASK}${quote}`;
    }
  );

  return masked.length > MAX_STRING_LENGTH
    ? `[длинное сообщение ошибки скрыто, длина ${masked.length}]`
    : masked;
};

/** Запросы интеграций: только они попадают в журнал, остальной трафик — мимо. */
const INTEGRATION_URL_PATTERN =
  /\/(SocialIntegrations|IntegrationAuth|SocialIntegrationEmailParsingRule)\//i;

const ACTION_BY_URL: Array<[RegExp, IntegrationActionKind]> = [
  [/RestartIntegration/i, 'restart'],
  [/RequestQr|RequestWhatsAppPairingCode/i, 'request-qr'],
  [/GetQr|RefreshIntegrationAuthData/i, 'get-qr'],
  [/PairingCode/i, 'pairing-code'],
  [/GetIntegrationAuthState/i, 'auth-state'],
  [/Edit|Update|Options/i, 'settings'],
  [/Add|Create/i, 'create'],
  [/Delete/i, 'delete'],
  [/SetUserActiveBranch/i, 'branch-switch'],
];

const detectKind = (url: string): IntegrationActionKind => {
  const found = ACTION_BY_URL.find(([pattern]) => pattern.test(url));

  return found ? found[1] : 'other';
};

export const isIntegrationRequest = (url?: string | null) =>
  Boolean(url && INTEGRATION_URL_PATTERN.test(url));

/**
 * Чтения списков страница делает постоянно, и их ответы — сотни килобайт
 * справочников. В файле от них один шум, поэтому у таких запросов оставляем
 * факт, код и длительность, а тело ответа не пишем.
 */
const LIST_READ_PATTERN =
  /(GetSocialIntegrations|GetSocialIntegrationOperators|GetSalesFunnels)/i;

const keepsResponseBody = (url: string, status?: number) =>
  !LIST_READ_PATTERN.test(url) || (status !== undefined && status >= 400);

/** Метка времени старта запроса — по ней считается длительность в ответе. */
const REQUEST_START_KEY = 'integrationLogStartedAt';

type TimedConfig = AxiosRequestConfig & {
  [REQUEST_START_KEY]?: number;
};

let isRecording = false;
let entries: IntegrationLogEntry[] = [];

const push = (entry: IntegrationLogEntry) => {
  entries.push(entry);
  if (entries.length > MAX_ENTRIES) {
    entries = entries.slice(-MAX_ENTRIES);
  }
};

const buildUrl = (config: AxiosRequestConfig) => {
  const base = config.url ?? '';

  return config.baseURL && !base.startsWith('http')
    ? `${config.baseURL}${base}`
    : base;
};

export const integrationActionLog = {
  get isRecording() {
    return isRecording;
  },

  /** Вызывается при входе в реестр интеграций. */
  start() {
    isRecording = true;
    entries = [];
  },

  /** Вызывается при уходе со страницы: запись прекращается, журнал очищается. */
  stop() {
    isRecording = false;
    entries = [];
  },

  /** Помечает время старта, чтобы в ответе посчитать длительность. */
  markRequest(config: AxiosRequestConfig) {
    if (!isRecording || !isIntegrationRequest(buildUrl(config))) return;

    (config as TimedConfig)[REQUEST_START_KEY] = Date.now();
  },

  recordResponse(response: AxiosResponse) {
    if (!isRecording) return;

    const rawUrl = buildUrl(response.config ?? {});
    if (!isIntegrationRequest(rawUrl)) return;

    const startedAt = (response.config as TimedConfig)?.[REQUEST_START_KEY];
    const url = maskUrlSecrets(rawUrl);

    push({
      at: new Date().toISOString(),
      kind: detectKind(url),
      method: (response.config?.method ?? 'get').toUpperCase(),
      url,
      params: maskValue(response.config?.params),
      body: maskRequestValue(rawUrl, response.config?.data),
      status: response.status,
      response: keepsResponseBody(url, response.status)
        ? maskResponseValue(rawUrl, response.data)
        : '[тело ответа не пишем: чтение списка]',
      durationMs: startedAt ? Date.now() - startedAt : undefined,
    });
  },

  recordFailure(config: AxiosRequestConfig | undefined, error: unknown) {
    if (!isRecording || !config) return;

    const rawUrl = buildUrl(config);
    if (!isIntegrationRequest(rawUrl)) return;

    const startedAt = (config as TimedConfig)[REQUEST_START_KEY];
    const response = (error as { response?: AxiosResponse })?.response;
    const url = maskUrlSecrets(rawUrl);

    push({
      at: new Date().toISOString(),
      kind: detectKind(url),
      method: (config.method ?? 'get').toUpperCase(),
      url,
      params: maskValue(config.params),
      body: maskRequestValue(rawUrl, config.data),
      status: response?.status,
      response: response ? maskResponseValue(rawUrl, response.data) : undefined,
      durationMs: startedAt ? Date.now() - startedAt : undefined,
      error:
        error instanceof Error
          ? maskErrorSecrets(error.message)
          : 'Запрос не дошёл до сервера',
    });
  },

  /**
   * Смена состояния интеграции, пришедшая помимо запроса пользователя (по
   * WebSocket): без неё в файле не видно, что происходило между действиями.
   */
  recordStatusChange(payload: unknown) {
    if (!isRecording) return;

    push({
      at: new Date().toISOString(),
      kind: 'status-change',
      method: 'WS',
      url: 'socialIntegrationsStatus',
      response: maskValue(payload),
    });
  },

  getEntries(): IntegrationLogEntry[] {
    return [...entries];
  },
};
